import React, { useState, useCallback, useImperativeHandle } from 'react';
import { CollapsiblePanelIcon } from '../../icons';

/**
 * Desktop (lg+) Inputs sidebar. Expanded state is local so toggling does not
 * re-render GenerationProfileChart. Panel content stays mounted (hidden when
 * collapsed) to avoid remount lag on expand.
 */
const DesktopInputsSidebar = React.memo(
  React.forwardRef(function DesktopInputsSidebar(
    { children, onExpandedChange, onLayoutChange },
    ref
  ) {
    const [expanded, setExpanded] = useState(() =>
      typeof window === 'undefined' || !window.matchMedia('(max-width: 1023px)').matches
    );

    const notifyAfterPaint = useCallback(() => {
      requestAnimationFrame(() => {
        onLayoutChange?.();
      });
    }, [onLayoutChange]);

    const setExpandedAndNotify = useCallback(
      (next) => {
        setExpanded(next);
        onExpandedChange?.(next);
        notifyAfterPaint();
      },
      [notifyAfterPaint, onExpandedChange]
    );

    useImperativeHandle(
      ref,
      () => ({
        collapse: () => setExpandedAndNotify(false),
        expand: () => setExpandedAndNotify(true),
        isExpanded: () => expanded,
      }),
      [expanded, setExpandedAndNotify]
    );

    return (
      <div
        className={`hidden lg:flex flex-shrink-0 flex-col overflow-hidden transition-none rounded-l-lg ${
          expanded ? 'w-62 xl:w-[300px]' : 'w-14 min-w-[3.5rem]'
        } ${!expanded ? 'bg-white' : ''}`}
      >
        {!expanded && (
          <button
            type="button"
            onClick={() => setExpandedAndNotify(true)}
            className="btn-panel-toggle-bar"
            title="Expand panel"
            aria-label="Expand panel"
          >
            <CollapsiblePanelIcon size={18} />
          </button>
        )}
        <div
          className={`flex-1 min-h-0 flex flex-col overflow-y-auto pt-0 space-y-2 ${
            expanded ? '' : 'hidden'
          }`}
          aria-hidden={!expanded}
        >
          {children}
        </div>
      </div>
    );
  })
);

export default DesktopInputsSidebar;
